'use client';

import { RoundRecord } from '@/types/game';
import { formatPrice, getFeedbackKey, getTier, TIER_TEXT } from '@/lib/game-logic';
import { useTranslation } from '@/lib/i18n/LanguageContext';
import { useCountUp } from '@/lib/useCountUp';
import { AffiliateLinks } from '@/components/AffiliateLinks';
import { DebugAffiliateLinks } from '@/components/DebugAffiliateLinks';
import { PriceGauge } from './PriceGauge';

interface RoundResultProps {
  record: RoundRecord;
  isLastRound: boolean;
  onNext: () => void;
}

export function RoundResult({ record, isLastRound, onNext }: RoundResultProps) {
  const { t } = useTranslation();
  const { sneaker, result } = record;
  const tier = getTier(result.percentageError);
  const points = useCountUp(result.pointsEarned);

  return (
    <section className="card anim-rise overflow-hidden" aria-live="polite">
      <div className="flex items-end justify-between gap-3 px-4 pt-4">
        <div className="min-w-0">
          <p className={`font-display text-lg font-extrabold leading-tight ${TIER_TEXT[tier]}`}>
            {t(getFeedbackKey(result.percentageError))}
          </p>
          <p className="mt-0.5 text-xs text-muted">
            {t('difference')} : <span className="font-mono text-fg">{formatPrice(result.difference)}</span> (
            {result.percentageError.toFixed(1)}%)
          </p>
        </div>
        <p className="anim-pop shrink-0 text-right font-display text-4xl font-extrabold tabular-nums leading-none sm:text-5xl">
          +{points}
          <span className="ml-1 text-[10px] font-semibold uppercase tracking-widest text-muted">{t('points')}</span>
        </p>
      </div>

      <PriceGauge guess={result.guessedPrice} actual={result.actualPrice} tier={tier} />

      <div className="grid grid-cols-2 gap-px border-y border-line bg-line">
        <div className="bg-surface px-4 py-2.5 text-center">
          <p className="text-[10px] uppercase tracking-widest text-muted">{t('yourAnswer')}</p>
          <p className="font-mono text-lg font-bold">{formatPrice(result.guessedPrice)}</p>
        </div>
        <div className="bg-surface px-4 py-2.5 text-center">
          <p className="text-[10px] uppercase tracking-widest text-accent">{t('actualPrice')}</p>
          <p className="font-mono text-lg font-bold text-accent">{formatPrice(result.actualPrice)}</p>
        </div>
      </div>

      <div className="space-y-3 p-4">
        <AffiliateLinks sneakerName={sneaker.name} brand={sneaker.brand} slug={sneaker.slug} />
        <DebugAffiliateLinks sneakerName={sneaker.name} brand={sneaker.brand} slug={sneaker.slug} />

        {/* No autoFocus here: the Enter that submitted the guess would "press" this button too */}
        <button onClick={onNext} className="btn-primary w-full py-3 text-base">
          {isLastRound ? t('viewFinalScore') : t('nextSneaker')} →
        </button>
      </div>
    </section>
  );
}
